import { Fragment, useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const statusOptions = [
 { value: "", label: "All Status" },
 { value: "PRESENT", label: "Present" },
 { value: "HALF_DAY", label: "Half Day" },
 { value: "MISSING_PUNCH_OUT", label: "Missing Punch Out" },
 { value: "ABSENT", label: "Absent" },
];
const statusVariant = { PRESENT: "success", HALF_DAY: "warning", MISSING_PUNCH_OUT: "warning", ABSENT: "danger", LEAVE: "info", WEEKLY_OFF: "secondary", HOLIDAY: "primary", MANUAL: "dark" };

const toDateInput = (date = new Date()) => {
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, "0");
 const day = String(date.getDate()).padStart(2, "0");
 return `${year}-${month}-${day}`;
};

const getDefaultFilters = () => {
 const today = new Date();
 return {
 employee: "",
 status: "",
 month: "",
 year: today.getFullYear(),
 fromDate: toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
 toDate: toDateInput(today),
 };
};

const formatDate = (value) => {
 if (!value) return "-";
 return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTime = (value) => {
 if (!value) return "-";
 return new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (minutes) => {
 const total = Number(minutes || 0);
 if (!total) return "0h";
 const hours = Math.floor(total / 60);
 const mins = total % 60;
 return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const getUser = (record) => record?.employeeId?.userId || {};
const getEmployeeId = (record) => String(record?.employeeId?._id || record?.employeeId || "");
const isMissingPunchOut = (record) => Boolean(record?.requiresReview || (record?.source === "SELF" && record?.punchInAt && !record?.punchOutAt));
const getDisplayStatusKey = (record) => (isMissingPunchOut(record) ? "MISSING_PUNCH_OUT" : String(record?.status || "PRESENT"));
const normalizeStatus = (status) => String(status || "PRESENT").replace(/_/g, " ");
const getDisplayStatus = (record) => normalizeStatus(getDisplayStatusKey(record));
const safeText = (value) => String(value ?? "-").replace(/[^\x20-\x7E]/g, " ");
const truncateText = (value, max = 24) => {
 const text = safeText(value);
 return text.length > max ? `${text.slice(0, Math.max(0, max - 3))}...` : text;
};
const sanitizeFileName = (value) => String(value || "export").replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
const downloadBlob = (blob, filename) => {
 const url = URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download = filename;
 document.body.appendChild(link);
 link.click();
 link.remove();
 URL.revokeObjectURL(url);
};

const getLateMinutes = (record, config) => {
 if (!record?.punchInAt || !record?.dateKey || !config?.officeStartTime) return 0;
 const [hours, minutes] = String(config.officeStartTime).split(":").map(Number);
 if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
 const allowed = new Date(`${record.dateKey}T00:00:00`);
 allowed.setHours(hours, minutes + Number(config.graceMinutes || 0), 0, 0);
 const punchIn = new Date(record.punchInAt);
 return punchIn > allowed ? Math.round((punchIn.getTime() - allowed.getTime()) / 60000) : 0;
};

const StaffAllAttendance = ({ embedded = false }) => {
 const navigate = useNavigate();
 const [records, setRecords] = useState([]);
 const [allEmployees, setAllEmployees] = useState(new Map());
 const [config, setConfig] = useState({ officeStartTime: "09:30", graceMinutes: 15 });
 const [loading, setLoading] = useState(false);
 const [exporting, setExporting] = useState("");
 const [filters, setFilters] = usePersistentFilters("staff_all_attendance_filters", getDefaultFilters);

 const loadAttendance = async (nextFilters = filters) => {
 try {
 setLoading(true);
 const params = {};
 if (nextFilters.status) params.status = nextFilters.status;
 if (nextFilters.employee) params.employeeId = nextFilters.employee;
 if (nextFilters.fromDate) params.fromDate = nextFilters.fromDate;
 if (nextFilters.toDate) params.toDate = nextFilters.toDate;
 if (!nextFilters.fromDate && !nextFilters.toDate && nextFilters.month !== "") {
 params.month = Number(nextFilters.month);
 params.year = Number(nextFilters.year || new Date().getFullYear());
 }

 const [punchRes, configRes] = await Promise.allSettled([
 api.get("/staff-attendance/punches", { params }),
 api.get("/staff-attendance/config"),
 ]);

 if (punchRes.status === "fulfilled" && punchRes.value.data.success) {
 const list = punchRes.value.data.data || [];
 setRecords(list);
 setAllEmployees((prev) => {
 const next = new Map(prev);
 list.forEach((record) => {
 const id = getEmployeeId(record);
 const user = getUser(record);
 if (id && !next.has(id)) next.set(id, user.name || user.email || "Staff");
 });
 return next;
 });
 } else {
 setRecords([]);
 swal("Error", "Failed to load staff attendance", "error");
 }

 if (configRes.status === "fulfilled" && configRes.value.data.success) {
 setConfig((current) => ({ ...current, ...(configRes.value.data.data || {}) }));
 }
 } catch (error) {
 swal("Error", "Failed to load staff attendance", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 const timer = setTimeout(() => loadAttendance(filters), 250);
 return () => clearTimeout(timer);
 }, [filters]);

 const employeeOptions = useMemo(() => {
 return Array.from(allEmployees.entries()).sort((a, b) => a[1].localeCompare(b[1]));
 }, [allEmployees]);

 const filteredRecords = records;

 const stats = useMemo(() => ({
 total: filteredRecords.length,
 present: filteredRecords.filter((record) => ["PRESENT", "HALF_DAY"].includes(record.status)).length,
 missingPunchOut: filteredRecords.filter((record) => isMissingPunchOut(record)).length,
 absent: filteredRecords.filter((record) => record.status === "ABSENT").length,
 halfDay: filteredRecords.filter((record) => record.status === "HALF_DAY").length,
 late: filteredRecords.filter((record) => getLateMinutes(record, config) > 0).length,
 }), [config, filteredRecords]);

 const exportRows = useMemo(() => filteredRecords.map((record, index) => {
 const user = getUser(record);
 const lateMinutes = getLateMinutes(record, config);
 return {
 "#": index + 1,
 Employee: user.name || "Staff",
 Email: user.email || "-",
 Level: record.employeeId?.level || "-",
 Date: formatDate(record.dateKey),
 "Punch In": formatTime(record.punchInAt),
 "Punch Out": formatTime(record.punchOutAt),
 Hours: formatDuration(record.totalMinutes),
 Status: getDisplayStatus(record),
 Review: isMissingPunchOut(record) ? "Required" : "-",
 Late: lateMinutes ? `${lateMinutes} min` : "-",
 };
 }), [config, filteredRecords]);

 const getExportFileBase = () => {
 const range = filters.month !== "" ? `${months[Number(filters.month)]}_${filters.year}` : `${filters.fromDate || "all"}_to_${filters.toDate || "all"}`;
 return sanitizeFileName(`Staff_All_Attendance_${range}`);
 };

 const handleExportExcel = async () => {
 try {
 setExporting("excel");
 const XLSX = await import("xlsx");
 const wb = XLSX.utils.book_new();
 const summaryRows = [
 ["All Attendance", getExportFileBase()],
 ["Total Records", stats.total],
 ["Present", stats.present],
 ["Absent", stats.absent],
 ["Half Day", stats.halfDay],
 ["Missing Punch Out", stats.missingPunchOut],
 ["Late", stats.late],
 ];
 XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
 XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), "Attendance");
 XLSX.writeFile(wb, `${getExportFileBase()}.xlsx`);
 } catch (error) {
 swal("Error", "Failed to export Excel", "error");
 } finally {
 setExporting("");
 }
 };

 const handleExportPdf = async () => {
 try {
 setExporting("pdf");
 const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
 const pdfDoc = await PDFDocument.create();
 const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
 const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
 const pageSize = [842, 595];
 const margin = 32;
 const rowHeight = 20;
 const columns = [
 { label: "#", key: "#", width: 28, max: 4 },
 { label: "Employee", key: "Employee", width: 130, max: 20 },
 { label: "Email", key: "Email", width: 150, max: 24 },
 { label: "Date", key: "Date", width: 78, max: 12 },
 { label: "In", key: "Punch In", width: 70, max: 10 },
 { label: "Out", key: "Punch Out", width: 70, max: 10 },
 { label: "Hours", key: "Hours", width: 62, max: 10 },
 { label: "Status", key: "Status", width: 92, max: 17 },
 { label: "Review", key: "Review", width: 58, max: 9 },
 { label: "Late", key: "Late", width: 48, max: 8 },
 ];

 let page;
 let y;
 const addPage = () => {
 page = pdfDoc.addPage(pageSize);
 y = pageSize[1] - margin;
 };
 const draw = (text, x, lineY, options = {}) => {
 page.drawText(safeText(text), {
 x,
 y: lineY,
 size: options.size || 8,
 font: options.bold ? boldFont : font,
 color: options.color || rgb(0.1, 0.12, 0.16),
 });
 };
 const drawTableHeader = () => {
 let x = margin;
 page.drawRectangle({ x: margin, y: y - 5, width: pageSize[0] - margin * 2, height: 16, color: rgb(0.92, 0.95, 0.99) });
 columns.forEach((col) => {
 draw(col.label, x + 3, y, { bold: true, size: 7 });
 x += col.width;
 });
 y -= rowHeight;
 };

 addPage();
 draw("All Attendance", margin, y, { bold: true, size: 16 });
 y -= 18;
 draw(`Records: ${stats.total} | Present: ${stats.present} | Half Day: ${stats.halfDay} | Missing Punch Out: ${stats.missingPunchOut} | Absent: ${stats.absent} | Late: ${stats.late}`, margin, y, { size: 9 });
 y -= 24;
 drawTableHeader();

 exportRows.forEach((row) => {
 if (y < margin + rowHeight) {
 addPage();
 drawTableHeader();
 }
 let x = margin;
 columns.forEach((col) => {
 draw(truncateText(row[col.key], col.max), x + 3, y, { size: 7 });
 x += col.width;
 });
 page.drawLine({ start: { x: margin, y: y - 5 }, end: { x: pageSize[0] - margin, y: y - 5 }, thickness: 0.3, color: rgb(0.86, 0.89, 0.94) });
 y -= rowHeight;
 });

 const bytes = await pdfDoc.save();
 downloadBlob(new Blob([bytes], { type: "application/pdf" }), `${getExportFileBase()}.pdf`);
 } catch (error) {
 swal("Error", "Failed to export PDF", "error");
 } finally {
 setExporting("");
 }
 };

  const clearFilters = () => {
    setFilters({
      employee: "",
      status: "",
      month: "",
      year: new Date().getFullYear(),
      fromDate: "",
      toDate: "",
    });
  };

 return (
 <Fragment>
 {!embedded && <PageTitle activeMenu="All Attendance" motherMenu="HR" />}

 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
 <div>
 <h3 className="mb-0">All Attendance</h3>
 <div className="text-muted small">Staff punch records and available attendance status</div>
 </div>
 <div className="d-flex gap-2 flex-wrap">
 {!embedded && <Button variant="outline-secondary" onClick={() => navigate(-1)}>Back</Button>}
 <Button variant="outline-secondary" onClick={clearFilters}>Clear Filter</Button>
 <Button
 variant="success"
 className="d-flex align-items-center gap-1 shadow-sm"
 onClick={() => navigate("/live-tracking")}
 >
 Live Tracking
 </Button>
 <Button variant="outline-danger" onClick={handleExportPdf} disabled={loading || exporting === "pdf"}>
 {exporting === "pdf" ? "Exporting..." : "Export PDF"}
 </Button>
 <Button variant="outline-success" onClick={handleExportExcel} disabled={loading || exporting === "excel"}>
 {exporting === "excel" ? "Exporting..." : "Export Excel"}
 </Button>
 </div>
 </div>

 <Row className="g-3 mb-3">
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-total"><Card.Body><span>Total Records</span><strong>{stats.total}</strong></Card.Body></Card></Col>
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-present"><Card.Body><span>Present</span><strong>{stats.present}</strong></Card.Body></Card></Col>
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-absent"><Card.Body><span>Absent</span><strong>{stats.absent}</strong></Card.Body></Card></Col>
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-half"><Card.Body><span>Half Day</span><strong>{stats.halfDay}</strong></Card.Body></Card></Col>
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-missing"><Card.Body><span>Missing Punch Out</span><strong>{stats.missingPunchOut}</strong></Card.Body></Card></Col>
 <Col xl={2} md={4}><Card className="h-100 staff-attendance-stat staff-attendance-stat-late"><Card.Body><span>Late</span><strong>{stats.late}</strong></Card.Body></Card></Col>
 </Row>

 <Card className="mb-3">
 <Card.Body>
 <h5 className="mb-3">Filters</h5>
 <Row className="g-2 align-items-end">
 <Col xl={2} md={4}>
 <Form.Label>Employee</Form.Label>
 <Form.Select value={filters.employee} onChange={(e) => setFilters({ ...filters, employee: e.target.value })}>
 <option value="">All Employees</option>
 {employeeOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
 </Form.Select>
 </Col>
 <Col xl={2} md={4}>
 <Form.Label>Status</Form.Label>
 <Form.Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
 {statusOptions.map((item) => <option key={item.value || "all"} value={item.value}>{item.label}</option>)}
 </Form.Select>
 </Col>
 <Col xl={2} md={4}>
 <Form.Label>Month</Form.Label>
 <Form.Select value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value, fromDate: "", toDate: "" })}>
 <option value="">Select Month</option>
 {months.map((item, index) => <option key={item} value={index}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col xl={2} md={4}>
 <Form.Label>Year</Form.Label>
 <Form.Select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
 {[2024, 2025, 2026, 2027, 2028].map((item) => <option key={item} value={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col xl={2} md={4}>
 <Form.Label>From Date</Form.Label>
 <Form.Control type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value, month: "" })} />
 </Col>
 <Col xl={2} md={4}>
 <Form.Label>To Date</Form.Label>
 <Form.Control type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value, month: "" })} />
 </Col>

 </Row>
 </Card.Body>
 </Card>

 <Card>
 <Card.Body className="p-0">
 {loading ? <div className="text-center py-5"><Spinner /></div> : (
            <Table responsive hover className="align-middle mb-0 staff-all-attendance-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Punch In</th>
                  <th>Punch Out</th>
                  <th>Hours</th>
                  <th>Photos</th>
                  <th>Status</th>
                  <th>Review</th>
                  <th>Late</th>
                  <th className="text-end">Live GPS</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr><td colSpan="10" className="text-center text-muted py-4">No attendance records found</td></tr>
                ) : filteredRecords.map((record) => {
                  const user = getUser(record);
                  const lateMinutes = getLateMinutes(record, config);
                  const staffUserId = user._id || record.employeeId?.userId?._id || record.employeeId?.userId || record.employeeId?._id;
                  return (
                    <tr key={record._id}>
                      <td>
                        <strong>{user.name || "Staff"}</strong>
                        <div className="text-muted small">{user.email || "-"}</div>
                        {record.employeeId?.level && <Badge bg="primary" className="mt-1">{record.employeeId.level}</Badge>}
                      </td>
                      <td>{formatDate(record.dateKey)}</td>
                      <td>{formatTime(record.punchInAt)}</td>
                      <td>{formatTime(record.punchOutAt)}</td>
                      <td>{formatDuration(record.totalMinutes)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {record.photoIn ? (
                            <a href={getFileUrl(record.photoIn)} target="_blank" rel="noreferrer" title="Punch In Photo">
                              <img src={getFileUrl(record.photoIn)} alt="In" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6", cursor: "pointer" }} />
                            </a>
                          ) : <span className="text-muted small">-</span>}
                          {record.photoOut ? (
                            <a href={getFileUrl(record.photoOut)} target="_blank" rel="noreferrer" title="Punch Out Photo">
                              <img src={getFileUrl(record.photoOut)} alt="Out" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6", cursor: "pointer" }} />
                            </a>
                          ) : null}
                        </div>
                      </td>
                      <td><Badge bg={statusVariant[getDisplayStatusKey(record)] || "secondary"}>{getDisplayStatus(record)}</Badge></td>
                      <td>{isMissingPunchOut(record) ? <Badge bg="warning" text="dark">Review</Badge> : "-"}</td>
                      <td>{lateMinutes ? `${lateMinutes} min` : "-"}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="d-flex align-items-center gap-1 ms-auto"
                          style={{ fontSize: 11, padding: "3px 8px", whiteSpace: "nowrap" }}
                          title="View live tracking & daily route trail"
                          onClick={() => navigate(`/live-tracking?staffId=${staffUserId || ""}&dateKey=${record.dateKey}`)}
                        >
                          Trail
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
 )}
 </Card.Body>
 </Card>

 <style>{`
 .staff-attendance-stat .card-body {
 min-height: 92px;
 display: flex;
 flex-direction: column;
 justify-content: center;
 gap: 4px;
 }
 .staff-attendance-stat span { color: #6b7280; font-size: 14px; }
 .staff-attendance-stat strong { color: #111827; font-size: 26px; line-height: 1; }
 .staff-attendance-stat-total { background: #eef2ff; border-color: #e2e8ff; }
 .staff-attendance-stat-present { background: #ecfdf3; border-color: #d7f8e3; }
 .staff-attendance-stat-absent { background: #fff1f2; border-color: #ffe1e5; }
 .staff-attendance-stat-half { background: #fff7ed; border-color: #ffedd5; }
 .staff-attendance-stat-late { background: #fffbeb; border-color: #fef3c7; }
 .staff-attendance-stat-missing { background: #fff7ed; border-color: #fed7aa; }
 .staff-all-attendance-table th { white-space: nowrap; font-size: 12px; text-transform: uppercase; color: #667085; }
 .staff-all-attendance-table td { vertical-align: middle; }
 [data-theme-version="dark"] .staff-attendance-stat { background: #1f1f1f; border-color: #30343b; }
 [data-theme-version="dark"] .staff-attendance-stat span { color: #9ca3af; }
 [data-theme-version="dark"] .staff-attendance-stat strong { color: #ffffff; }
 `}</style>
 </Fragment>
 );
};

export default StaffAllAttendance;





